import { useMemo, useRef } from 'react';
import {
  flexRender,
  getCoreRowModel,
  useReactTable,
  type ColumnDef,
} from '@tanstack/react-table';
import { AlertCircleIcon, InboxIcon } from 'lucide-react';

import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Checkbox } from '@/components/ui/checkbox';
import { EmptyState } from '@/components/empty-state';
import { useT } from '@/lib/i18n';
import { useRowShortcuts } from '@/lib/shortcuts';
import { cn } from '@/lib/utils';

/**
 * `<DataTable>` — the console's ONE list mechanism (build/10 §5).
 *
 * The item bank, candidates, audit, users and results lists are all instances
 * of this. Every screen that has wanted a table since Phase 0 deferred to it
 * rather than rolling its own, which is why Notifications is a feed and the
 * Settings tabs are header-only tables — a second table would have either
 * duplicated this or pre-empted its API.
 *
 * THE THREE STATES ARE MANDATORY AND NOT OPTIONAL PROPS. `10 §5` requires
 * skeleton, empty and error on every data surface, so they are parameters of
 * the component rather than something each caller remembers. The failure this
 * prevents is the one that has already happened twice in this console: a screen
 * that renders "nothing here" identically whether the list is empty or the API
 * is unreachable.
 *
 * PAGINATION IS SERVER-SIDE. The bank is ~600 items and the contract forbids
 * returning a whole collection, so this never receives more than a page and
 * never sorts or filters across pages client-side — doing so would silently
 * sort one page and look like it sorted everything.
 */
export interface DataTableProps<T> {
  columns: ColumnDef<T, unknown>[];
  rows: T[];

  /** The three states, all required. */
  isLoading?: boolean;
  error?: unknown;
  onRetry?: () => void;

  /**
   * Shown when the query succeeded and returned nothing.
   *
   * Still plain strings, not string keys: the empty state is the one part of a
   * list only the calling screen can write ("No certified items in this module"
   * says something a generic table cannot), so callers translate and pass text.
   * The three states this component owns — error, loading, paging — it
   * translates itself.
   */
  emptyTitle: string;
  emptyDescription: string;
  emptyAction?: React.ReactNode;

  /** Server-side paging. Omit for a bounded list (personas, matrix cells). */
  page?: number;
  pageSize?: number;
  total?: number;
  onPageChange?: (page: number) => void;

  onRowClick?: (row: T) => void;
  /** Column count for the skeleton, before any data exists to measure. */
  skeletonRows?: number;

  /**
   * Row selection (`plan-beta` B5). Omit and the table has no checkbox column.
   *
   * Selection is held by the CALLER, not here. A bulk action needs the ids after
   * the table has re-rendered, re-sorted or been paged, and a component that
   * owned them would have to publish them back anyway — so the one place they
   * live is the screen that acts on them.
   */
  selection?: {
    selected: Set<string>;
    onChange: (next: Set<string>) => void;
    idOf: (row: T) => string;
  };
}

export function DataTable<T>({
  columns,
  rows,
  isLoading = false,
  error,
  onRetry,
  emptyTitle,
  emptyDescription,
  emptyAction,
  page,
  pageSize,
  total,
  onPageChange,
  onRowClick,
  skeletonRows = 8,
  selection,
}: DataTableProps<T>) {
  const t = useT();
  /**
   * `j`/`k` move between rows and `Enter` opens one — but only where a row IS
   * openable. A table whose rows do nothing gets no row cursor, because a focus
   * ring you cannot act on is noise.
   */
  const body = useRef<HTMLTableSectionElement>(null);
  useRowShortcuts(onRowClick ? body : { current: null });

  const table = useReactTable({
    data: rows,
    columns,
    getCoreRowModel: getCoreRowModel(),
    // Server-side: the table renders the page it was handed and does not
    // paginate, sort or filter it further.
    manualPagination: true,
    manualSorting: true,
    manualFiltering: true,
  });

  /**
   * "Select all" MEANS THIS PAGE, and the label says so.
   *
   * Pagination is server-side, so the rows here are the only ones the browser
   * has. A header checkbox that implied the whole filtered set would be
   * promising an action over items nobody has seen — and the bulk endpoint is
   * capped at 200 anyway. Selecting across pages accumulates, because the
   * caller holds the set: tick a page, go to the next, tick again.
   */
  const pageIds = useMemo(
    () => (selection ? rows.map(selection.idOf) : []),
    [rows, selection],
  );
  const allOnPage = pageIds.length > 0 && pageIds.every((id) => selection!.selected.has(id));

  const togglePage = (on: boolean) => {
    const next = new Set(selection!.selected);
    for (const id of pageIds) {
      if (on) next.add(id);
      else next.delete(id);
    }
    selection!.onChange(next);
  };

  const toggleRow = (id: string, on: boolean) => {
    const next = new Set(selection!.selected);
    if (on) next.add(id);
    else next.delete(id);
    selection!.onChange(next);
  };

  const totalPages = useMemo(
    () => (total && pageSize ? Math.max(1, Math.ceil(total / pageSize)) : 1),
    [total, pageSize],
  );

  /* ---------------------------------------------------------------- error */
  // Checked BEFORE empty: an unreachable API has no rows either, and reporting
  // that as "no records" is the confusion this component exists to prevent.
  if (error) {
    return (
      <EmptyState
        icon={AlertCircleIcon}
        title={t('table.errorTitle')}
        // The server's message is NOT translated — it is a diagnostic carrying a
        // traceId's worth of meaning, and a paraphrase of it in another language
        // is not the thing an operator quotes in a bug report.
        description={error instanceof Error ? error.message : t('table.errorDescription')}
        actions={onRetry ? <Button onClick={onRetry}>{t('common.retry')}</Button> : undefined}
      />
    );
  }

  /* -------------------------------------------------------------- loading */
  if (isLoading) {
    return (
      <div className="space-y-2" aria-busy="true" aria-live="polite">
        {Array.from({ length: skeletonRows }, (_, i) => (
          <Skeleton key={i} className="h-11 w-full" />
        ))}
      </div>
    );
  }

  /* ---------------------------------------------------------------- empty */
  if (rows.length === 0) {
    return (
      <EmptyState icon={InboxIcon} title={emptyTitle} description={emptyDescription} actions={emptyAction} />
    );
  }

  /* ----------------------------------------------------------------- data */
  return (
    <div className="space-y-4">
      {/* Wide tables scroll inside their own container; the page never scrolls
          horizontally (10 §2). */}
      <div className="overflow-x-auto rounded-md border">
        <Table>
          <TableHeader>
            {table.getHeaderGroups().map((group) => (
              <TableRow key={group.id}>
                {selection && (
                  <TableHead className="w-10">
                    <Checkbox
                      checked={allOnPage}
                      aria-label={t('table.selectPage')}
                      onCheckedChange={(on) => togglePage(on === true)}
                    />
                  </TableHead>
                )}
                {group.headers.map((header) => (
                  <TableHead key={header.id}>
                    {header.isPlaceholder
                      ? null
                      : flexRender(header.column.columnDef.header, header.getContext())}
                  </TableHead>
                ))}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody ref={body}>
            {table.getRowModel().rows.map((row) => (
              <TableRow
                key={row.id}
                onClick={onRowClick ? () => onRowClick(row.original) : undefined}
                // Focusable and Enter-activatable, so the row cursor is the
                // browser's own focus rather than a second one React invented —
                // which also makes it work for a screen reader and for Tab.
                {...(onRowClick
                  ? {
                      'data-row-focus': true,
                      tabIndex: 0,
                      role: 'button' as const,
                      onKeyDown: (e: React.KeyboardEvent) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          onRowClick(row.original);
                        }
                      },
                    }
                  : {})}
                className={cn(
                  onRowClick && 'hover:bg-muted/50 focus-visible:bg-muted/50 cursor-pointer outline-none',
                )}
              >
                {selection && (
                  <TableCell
                    className="w-10"
                    // The checkbox is not a row activation, and a table whose
                    // rows navigate would otherwise open one on every tick.
                    onClick={(e) => e.stopPropagation()}
                  >
                    <Checkbox
                      checked={selection.selected.has(selection.idOf(row.original))}
                      aria-label={t('table.selectRow')}
                      onCheckedChange={(on) =>
                        toggleRow(selection.idOf(row.original), on === true)}
                    />
                  </TableCell>
                )}
                {row.getVisibleCells().map((cell) => (
                  <TableCell key={cell.id}>
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {page !== undefined && total !== undefined && totalPages > 1 && (
        <div className="flex items-center justify-between text-sm">
          <p className="text-muted-foreground">
            {t('table.page', { page, pages: totalPages, total })}
          </p>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={page <= 1}
              onClick={() => onPageChange?.(page - 1)}
            >
              {t('common.previous')}
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={page >= totalPages}
              onClick={() => onPageChange?.(page + 1)}
            >
              {t('common.next')}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
